import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  Plus, Trash2, Save, Users, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle2, Edit3, DollarSign,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { cn } from '@/shared/utils/utils';
import {
  usePayrollEmployees,
  useMonthlyPayrollInput,
  useSaveMonthlyInputBulk,
  useGenerateBulkPayslips,
} from './hooks/usePayroll';
import type { PayrollEmployee, MonthlyInputLine } from './payrollTypes';

// ─── Types for local draft state ─────────────────────────────────────────────

interface EmployeeDraft {
  user_id: string;
  display_name: string;
  email: string;
  department: string;
  position: string;
  base_salary: number;
  earnings: MonthlyInputLine[];
  deductions: MonthlyInputLine[];
  notes: string;
  isDirty: boolean;
  isExpanded: boolean;
  hasSavedInput: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(n: number): string {
  return `\u20B9${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}

function totalEarnings(lines: MonthlyInputLine[]): number {
  return lines.reduce((sum, l) => sum + (l.amount || 0), 0);
}

function totalDeductions(lines: MonthlyInputLine[]): number {
  return lines.reduce((sum, l) => sum + (l.amount || 0), 0);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PayrollMonthlyInputTab() {
  const [monthYear, setMonthYear] = useState(format(new Date(), 'yyyy-MM'));
  const [drafts, setDrafts] = useState<EmployeeDraft[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [lastLoadedMonth, setLastLoadedMonth] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: employees, isLoading: loadingEmployees } = usePayrollEmployees();
  const { data: savedInputs, isLoading: loadingInputs } = useMonthlyPayrollInput(monthYear);
  const saveBulk = useSaveMonthlyInputBulk();
  const generatePayslips = useGenerateBulkPayslips();

  // Build drafts from employees + saved inputs
  const buildDrafts = useCallback(() => {
    if (!employees) return;
    const inputMap = new Map<string, { earnings: MonthlyInputLine[]; deductions: MonthlyInputLine[]; notes: string }>();
    if (savedInputs) {
      for (const input of savedInputs) {
        const userId = typeof input.user_id === 'object' && input.user_id !== null
          ? (input.user_id as { _id?: string; id?: string })._id || (input.user_id as { _id?: string; id?: string }).id || ''
          : String(input.user_id);
        if (userId) {
          inputMap.set(userId, {
            earnings: input.earnings || [],
            deductions: input.deductions || [],
            notes: input.notes || '',
          });
        }
      }
    }

    const newDrafts: EmployeeDraft[] = employees.map((emp: PayrollEmployee) => {
      const saved = inputMap.get(emp.id);
      return {
        user_id: emp.id,
        display_name: emp.display_name,
        email: emp.email || '',
        department: emp.department || '',
        position: emp.position || '',
        base_salary: emp.base_salary || 0,
        earnings: saved?.earnings ?? [],
        deductions: saved?.deductions ?? [],
        notes: saved?.notes ?? '',
        isDirty: false,
        isExpanded: false,
        hasSavedInput: !!saved,
      };
    });

    setDrafts(newDrafts);
    setInitialized(true);
    setLastLoadedMonth(monthYear);
  }, [employees, savedInputs, monthYear]);

  // Auto-initialize when data arrives or month changes
  if (employees && !loadingInputs && (!initialized || lastLoadedMonth !== monthYear)) {
    buildDrafts();
  }

  // Filtered drafts
  const filteredDrafts = useMemo(() => {
    if (!searchQuery.trim()) return drafts;
    const q = searchQuery.toLowerCase();
    return drafts.filter(d =>
      d.display_name.toLowerCase().includes(q) ||
      d.email.toLowerCase().includes(q) ||
      d.department.toLowerCase().includes(q)
    );
  }, [drafts, searchQuery]);

  // Count stats
  const dirtyCount = drafts.filter(d => d.isDirty).length;
  const filledCount = drafts.filter(d => d.earnings.length > 0 || d.deductions.length > 0).length;

  // ─── Handlers ────────────────────────────────────────────────────────────

  const toggleExpand = (userId: string) => {
    setDrafts(prev => prev.map(d =>
      d.user_id === userId ? { ...d, isExpanded: !d.isExpanded } : d
    ));
  };

  const updateEarnings = (userId: string, earnings: MonthlyInputLine[]) => {
    setDrafts(prev => prev.map(d =>
      d.user_id === userId ? { ...d, earnings, isDirty: true } : d
    ));
  };

  const updateDeductions = (userId: string, deductions: MonthlyInputLine[]) => {
    setDrafts(prev => prev.map(d =>
      d.user_id === userId ? { ...d, deductions, isDirty: true } : d
    ));
  };

  const updateNotes = (userId: string, notes: string) => {
    setDrafts(prev => prev.map(d =>
      d.user_id === userId ? { ...d, notes, isDirty: true } : d
    ));
  };

  const handleSaveAll = async () => {
    const dirtyDrafts = drafts.filter(d => d.isDirty);
    if (dirtyDrafts.length === 0) {
      toast('No changes to save.', { icon: '💡' });
      return;
    }

    try {
      const result = await saveBulk.mutateAsync({
        month_year: monthYear,
        inputs: dirtyDrafts.map(d => ({
          user_id: d.user_id,
          earnings: d.earnings.filter(e => e.label.trim() && e.amount > 0),
          deductions: d.deductions.filter(e => e.label.trim() && e.amount > 0),
          notes: d.notes,
        })),
      });

      if (result.errors?.length > 0) {
        toast.error(`Saved ${result.saved} but ${result.errors.length} failed.`);
      } else {
        toast.success(`Saved payroll input for ${result.saved} employee(s).`);
      }

      // Mark saved drafts as clean
      setDrafts(prev => prev.map(d => {
        if (dirtyDrafts.some(dd => dd.user_id === d.user_id)) {
          return { ...d, isDirty: false, hasSavedInput: true };
        }
        return d;
      }));
    } catch {
      toast.error('Failed to save payroll input.');
    }
  };

  const handleGeneratePayslips = async () => {
    if (dirtyCount > 0) {
      toast.error('Please save all changes before generating payslips.');
      return;
    }
    if (!confirm(`Generate draft payslips for ${monthYear}? Employees with monthly input data will use those values. Others will use formula-based calculation from their base salary.`)) {
      return;
    }
    try {
      const result = await generatePayslips.mutateAsync(monthYear);
      toast.success(result.message || 'Payslips generated.');
    } catch {
      toast.error('Failed to generate payslips.');
    }
  };

  // ─── Loading state ───────────────────────────────────────────────────────

  if (loadingEmployees || loadingInputs) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-10 bg-surface-variant animate-pulse rounded-lg" />
        <div className="h-64 bg-surface-variant animate-pulse rounded-lg" />
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="font-semibold text-on-surface text-headline-sm">Monthly Payroll Input</h3>
          <p className="text-body-md text-on-surface-variant mt-0.5">
            Enter per-employee earnings and deductions for the selected month. Then generate payslips.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            type="month"
            value={monthYear}
            onChange={(e) => {
              setMonthYear(e.target.value);
              setInitialized(false);
            }}
            className="w-40"
          />
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-surface-container-low/50 px-4 py-3">
        <div className="flex items-center gap-2 text-body-md">
          <Users className="h-4 w-4 text-primary" />
          <span className="text-on-surface-variant">{drafts.length} employees</span>
        </div>
        <div className="flex items-center gap-2 text-body-md">
          <CheckCircle2 className="h-4 w-4 text-success-text" />
          <span className="text-on-surface-variant">{filledCount} with input data</span>
        </div>
        {dirtyCount > 0 && (
          <div className="flex items-center gap-2 text-body-md">
            <AlertCircle className="h-4 w-4 text-warning-text" />
            <span className="text-warning-text dark:text-warning-text font-medium">{dirtyCount} unsaved</span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveAll}
            disabled={dirtyCount === 0 || saveBulk.isPending}
          >
            <Save className="h-3.5 w-3.5" />
            {saveBulk.isPending ? 'Saving...' : `Save All (${dirtyCount})`}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleGeneratePayslips}
            disabled={generatePayslips.isPending || dirtyCount > 0}
          >
            <DollarSign className="h-3.5 w-3.5" />
            {generatePayslips.isPending ? 'Generating...' : 'Generate Payslips'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <Input
        placeholder="Search employees by name, email, or department..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="max-w-sm"
      />

      {/* Employee list */}
      <div className="space-y-2 max-h-[560px] overflow-y-auto custom-scrollbar pr-1">
        {filteredDrafts.length === 0 && (
          <div className="text-center py-12 text-on-surface-variant">
            {searchQuery ? 'No employees match your search.' : 'No active employees found.'}
          </div>
        )}
        {filteredDrafts.map((draft) => (
          <EmployeeInputCard
            key={draft.user_id}
            draft={draft}
            onToggle={() => toggleExpand(draft.user_id)}
            onUpdateEarnings={(earnings) => updateEarnings(draft.user_id, earnings)}
            onUpdateDeductions={(deductions) => updateDeductions(draft.user_id, deductions)}
            onUpdateNotes={(notes) => updateNotes(draft.user_id, notes)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Employee Input Card ─────────────────────────────────────────────────────

interface EmployeeInputCardProps {
  draft: EmployeeDraft;
  onToggle: () => void;
  onUpdateEarnings: (earnings: MonthlyInputLine[]) => void;
  onUpdateDeductions: (deductions: MonthlyInputLine[]) => void;
  onUpdateNotes: (notes: string) => void;
}

function EmployeeInputCard({ draft, onToggle, onUpdateEarnings, onUpdateDeductions, onUpdateNotes }: EmployeeInputCardProps) {
  const gross = totalEarnings(draft.earnings);
  const deduct = totalDeductions(draft.deductions);
  const net = gross - deduct;
  const hasData = draft.earnings.length > 0 || draft.deductions.length > 0;

  return (
    <div className={cn(
      'rounded-lg border transition-all',
      draft.isDirty
        ? 'border-warning/30 dark:border-amber-700 bg-warning/10 dark:bg-amber-900/10'
        : hasData
          ? 'border-success/30 dark:border-emerald-800 bg-success/10 dark:bg-emerald-900/10'
          : 'border-outline bg-surface',
    )}>
      {/* Collapsed row */}
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-variant/40 rounded-lg transition-colors"
        onClick={onToggle}
        aria-expanded={draft.isExpanded}
      >
        {draft.isExpanded
          ? <ChevronDown className="h-4 w-4 text-on-surface-variant shrink-0" />
          : <ChevronRight className="h-4 w-4 text-on-surface-variant shrink-0" />
        }
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-on-surface text-body-md truncate">{draft.display_name}</span>
            {draft.isDirty && <span className="w-2 h-2 rounded-full bg-warning shrink-0" title="Unsaved changes" />}
            {draft.hasSavedInput && !draft.isDirty && (
              <CheckCircle2 className="h-3.5 w-3.5 text-success-text shrink-0" />
            )}
          </div>
          <div className="text-body-sm text-on-surface-variant truncate">
            {draft.department}{draft.position ? ` \u00B7 ${draft.position}` : ''}
            {draft.base_salary > 0 && ` \u00B7 Base: ${fmtCurrency(draft.base_salary)}`}
          </div>
        </div>
        {hasData && (
          <div className="flex items-center gap-4 text-body-sm shrink-0">
            <span className="text-success-text  font-medium">+{fmtCurrency(gross)}</span>
            <span className="text-danger-text  font-medium">-{fmtCurrency(deduct)}</span>
            <span className="font-semibold text-on-surface">Net: {fmtCurrency(net)}</span>
          </div>
        )}
        {!hasData && (
          <span className="text-body-sm text-on-surface-variant italic shrink-0">No input yet</span>
        )}
      </button>

      {/* Expanded form */}
      {draft.isExpanded && (
        <div className="px-4 pb-4 pt-1 border-t border-outline/50 space-y-4">
          <div className="flex justify-between items-center bg-blue-50/60 border border-blue-200 p-2 rounded-md">
            <div>
              <span className="text-xs font-semibold text-blue-900 block">Annexure - 1 Components Quick Preset</span>
              <span className="text-[11px] text-blue-700">Pre-fill Basic, HRA, Medical, Conveyance, Allowances, PT &amp; Insurance</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                onUpdateEarnings([
                  { label: 'Basic Salary', amount: 9311 },
                  { label: 'House Rent Allowance (HRA)', amount: 4656 },
                  { label: 'Conveyance Allowance', amount: 1000 },
                  { label: 'Medical Allowance', amount: 833 },
                  { label: 'Special Allowance', amount: 1487 },
                  { label: 'Education Allowance', amount: 200 },
                  { label: 'Other Allowance', amount: 1135 },
                ]);
                onUpdateDeductions([
                  { label: 'Insurance', amount: 422 },
                  { label: 'Professional Tax', amount: 200 },
                ]);
              }}
              className="text-xs text-blue-800 border-blue-300 hover:bg-blue-100"
            >
              Apply Annexure-1 Components
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Earnings */}
            <LineItemEditor
              title="Earnings"
              items={draft.earnings}
              onChange={onUpdateEarnings}
              accentColor="emerald"
            />
            {/* Deductions */}
            <LineItemEditor
              title="Deductions"
              items={draft.deductions}
              onChange={onUpdateDeductions}
              accentColor="rose"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-body-sm font-medium text-on-surface-variant mb-1 block">Notes (optional)</label>
            <Input
              placeholder="Any remarks for this month..."
              value={draft.notes}
              onChange={(e) => onUpdateNotes(e.target.value)}
              className="text-body-md"
            />
          </div>

          {/* Summary */}
          {hasData && (
            <div className="flex items-center justify-end gap-6 pt-2 border-t border-outline/30 text-body-md">
              <span className="text-on-surface-variant">
                Gross: <span className="font-semibold text-success-text ">{fmtCurrency(gross)}</span>
              </span>
              <span className="text-on-surface-variant">
                Deductions: <span className="font-semibold text-danger-text ">{fmtCurrency(deduct)}</span>
              </span>
              <span className="text-on-surface font-semibold">
                Net Pay: {fmtCurrency(net)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Line Item Editor (Earnings or Deductions) ───────────────────────────────

interface LineItemEditorProps {
  title: string;
  items: MonthlyInputLine[];
  onChange: (items: MonthlyInputLine[]) => void;
  accentColor: 'emerald' | 'rose';
}

function LineItemEditor({ title, items, onChange, accentColor }: LineItemEditorProps) {
  const addItem = () => {
    onChange([...items, { label: '', amount: 0 }]);
  };

  const updateItem = (index: number, field: 'label' | 'amount', value: string | number) => {
    const updated = items.map((item, i) => {
      if (i !== index) return item;
      if (field === 'amount') return { ...item, amount: Number(value) || 0 };
      return { ...item, label: String(value) };
    });
    onChange(updated);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const total = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  const colorClasses = accentColor === 'emerald'
    ? { header: 'text-success-text ', badge: 'bg-emerald-100 text-success-text dark:bg-emerald-900/40 ' }
    : { header: 'text-danger-text ', badge: 'bg-danger/10 text-danger-text dark:bg-rose-900/40 ' };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className={cn('text-body-md font-semibold', colorClasses.header)}>
          <Edit3 className="inline h-3.5 w-3.5 mr-1" />
          {title}
        </h4>
        {total > 0 && (
          <span className={cn('text-body-sm px-2 py-0.5 rounded-full font-medium', colorClasses.badge)}>
            {fmtCurrency(total)}
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              placeholder="Label (e.g. Basic Pay)"
              value={item.label}
              onChange={(e) => updateItem(i, 'label', e.target.value)}
              className="flex-1 text-body-md h-8"
            />
            <Input
              type="number"
              placeholder="Amount"
              value={item.amount || ''}
              onChange={(e) => updateItem(i, 'amount', e.target.value)}
              className="w-28 text-body-md h-8"
              min={0}
            />
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="p-1.5 text-on-surface-variant hover:text-danger-text rounded transition-colors"
              aria-label={`Remove ${title} line`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={addItem} className="text-body-sm h-7">
        <Plus className="h-3 w-3" /> Add {title === 'Earnings' ? 'earning' : 'deduction'}
      </Button>
    </div>
  );
}
